import React, { useEffect, useRef, useState } from "react";
import { Loader } from "@mantine/core";
import useStartupStore from "../stores/startupStore";
import useUserStore from "../stores/userStore";
import MarkdownIt from "markdown-it";
import config from "../components/Lib/environment";
import trackAiUsage from "../components/Lib/trackAiUsage";
import styled from "styled-components";

const IconBrain = () => ( // Example Icon
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-700"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v1.104a5.5 5.5 0 0 1 0 10.792V17.5a2.5 2.5 0 0 1-5 0v-1.104a5.5 5.5 0 0 1 0-10.792V4.5A2.5 2.5 0 0 1 9.5 2zM14.5 2a2.5 2.5 0 0 1 2.5 2.5v1.104a5.5 5.5 0 0 1 0 10.792V17.5a2.5 2.5 0 0 1-5 0v-1.104a5.5 5.5 0 0 1 0-10.792V4.5A2.5 2.5 0 0 1 14.5 2z"/></svg>
);
const IconChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
);
const IconChevronUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
);
const IconOpen = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3h7v7"/><path d="M10 14 21 3"/><path d="M5 7v14h14"/></svg>
);
const IconClose = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);
const IconSend = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9 22 2Z"/></svg>
);

const ChatBubble = styled.div`
  /* Base bubble styling */
  max-width: 75%; /* Slightly increased max-width for potentially longer messages */
  padding: 0.75rem 1rem; /* Slightly more vertical padding (py-3) */
  border-radius: 0.5rem; /* Softer corners (rounded-lg) */
  background-color: #ffffff; /* bg-white */
  /* Replaced border with a subtle shadow for depth */
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.07), 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  overflow-wrap: break-word;
  color: #1f2937; /* Darker base text (text-gray-800) for better contrast */
  line-height: 1.6; /* Default line height for readability */
  font-size: 0.95rem; /* Slightly larger base font size */

  /* Direct descendant paragraphs */
  & > p {
    margin-top: 0;
    margin-bottom: 0.75rem; /* Space below paragraphs */
    &:last-child {
      margin-bottom: 0; /* No margin for the very last element */
    }
  }

  /* Blockquote styling */
  blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    background-color: #f9fafb; /* bg-gray-50 */
    border-left: 4px solid #d1d5db; /* border-gray-300 */
    color: #4b5563; /* text-gray-600 */
    font-style: italic;
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Code block styling */
  pre {
    background: #f3f4f6; /* bg-gray-100 */
    color: #1f2937; /* text-gray-800 */
    padding: 0.75rem; /* Consistent padding */
    border-radius: 0.375rem; /* rounded-md */
    font-size: 0.875rem; /* text-sm */
    line-height: 1.4;
    overflow-x: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    margin: 1em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Inline code styling */
  code {
    background: #e5e7eb; /* bg-gray-200 */
    color: #1f2937; /* text-gray-800 */
    padding: 0.15em 0.4em; /* Slightly adjust padding */
    border-radius: 0.25rem; /* rounded-sm */
    font-size: 0.875em; /* Relative to surrounding text */
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  }

  /* Code within a pre block shouldn't have extra background/padding */
  pre code {
    background: none;
    padding: 0;
    border-radius: 0;
    font-size: inherit; /* Inherit size from pre */
  }

  /* List styling */
  ul,
  ol {
    margin: 1em 0;
    padding-left: 1.5rem; /* Standard indent */
    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    margin-bottom: 0.5rem; /* Space between list items */
  }

  /* Table styling */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 0.875rem; /* text-sm */
    color: #374151; /* text-gray-700 */
    border: 1px solid #e5e7eb; /* border-gray-200 */
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Headings - Cleaner, no underline */
  h1, h2, h3, h4, h5, h6 {
    color: #111827; /* text-gray-900 */
    margin-top: 1.25em;
    margin-bottom: 0.6em;
    font-weight: 600; /* semibold */
    line-height: 1.3;
    &:first-child {
      margin-top: 0;
    }
  }
  h1 { font-size: 1.5em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.15em; }
  h4, h5, h6 { font-size: 1em; }


  th,
  td {
    padding: 0.6rem 0.9rem; /* Adjust padding */
    border: 1px solid #e5e7eb; /* border-gray-200 */
    text-align: left;
  }

  th {
    background-color: #f9fafb; /* bg-gray-50 */
    font-weight: 600; /* semibold */
  }

  /* Link styling - Use teal accent */
  a {
    color: #0d9488; /* text-teal-600 */
    text-decoration: none; /* Remove default underline */
    cursor: pointer;
    font-weight: 500; /* medium */

    &:hover {
      color: #0f766e; /* text-teal-700 */
      text-decoration: underline; /* Underline on hover */
    }
  }

  /* Image styling */
  img {
    max-width: 100%;
    border-radius: 0.375rem; /* rounded-md */
    margin: 1em 0;
    display: block;
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Horizontal rule styling */
  hr {
    border: 0;
    border-top: 1px solid #e5e7eb; /* border-gray-200 */
    margin: 1.5em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Ensure last element inside bubble doesn't have bottom margin */
  & > *:last-child {
    margin-bottom: 0;
  }
`;

const ChatDocumentUpload = ({ isOpen = false, onClose = () => {}, anchored = true }) => {
  const [chats, setChats] = useState([]);
  const [userMessage, setUserMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const mdParser = new MarkdownIt();
  const chatMessagesRef = useRef(null);
  const chatInputRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isPopup, setIsPopup] = useState(false);
  const [usageError, setUsageError] = useState("");

  // Assuming the user store returns an object with a `user` property.
  const user = useUserStore((state) => state.user);
  const {
    content,
    roadmapId,
    selectedProgram,
    setChats: updateStoreChats,
  } = useStartupStore((state) => ({
    content: state.content,
    selectedProgram: state.selectedProgram,
    roadmapId: state.roadmapId,
    setChats: state.setChats,
  }));

  const pageId = selectedProgram.cid;
  const index = selectedProgram.sid;

  useEffect(() => {
    if (content[pageId]?.[index]?.chats) {
      setChats(content[pageId][index].chats);
    } else {
      setChats([]);
    }
  }, [pageId, index, content]);

  const fetchAIResponseStream = async (userId, projectId, userMessage) => {
    console.log(content);
    try {
      const response = await fetch(config.chatUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: "1",
          project_id: projectId,
          roadmap_id: roadmapId,
          message: userMessage,
          selected_step_number: pageId + 1,
          temperature: 0.7,
          category_id: content[pageId]?.[index]?.category_id + "",
          subcategory_id: content[pageId]?.[index]?.id + "",
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      return response;
    } catch (error) {
      console.error("Failed to fetch AI response", error);
      return null;
    }
  };

  const handleSendMessage = async () => {
    if (!userMessage.trim()) return;

    try {
      await trackAiUsage("smart_assistant");
      setUsageError("");
    } catch (error) {
      setUsageError(error.message || "AI usage limit reached.");
      return;
    }

    // Ensure the chat opens as a popup on first interaction
    setIsExpanded(true);
    setIsPopup(true);

    // Append the user's message and a placeholder for the assistant's reply in one update.
    setChats((prevChats) => {
      const newChats = [
        ...prevChats,
        [{ role: "user", content: userMessage }],
        [{ role: "assistant", content: "" }],
      ];
      updateStoreChats(newChats);
      return newChats;
    });
    setUserMessage("");
    setChatLoading(true);

    try {
      const response = await fetchAIResponseStream(
        `user_${user.id}`,
        "ecommerce-retail-usa-2025",
        userMessage
      );
      if (!response) {
        throw new Error("No response from server");
      }

      // Use the last chat in the state (assistant's placeholder) for streaming updates.
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        fullResponse += chunk;

        setChats((prevChats) => {
          const newChats = [...prevChats];
          // Always update the last element, which is the assistant's message.
          newChats[newChats.length - 1][0].content = fullResponse;
          updateStoreChats(newChats);
          return newChats;
        });
      }

      // Ensure the store has the final chats.
      setChats((prevChats) => {
        updateStoreChats(prevChats);
        return prevChats;
      });
    } catch (error) {
      console.error("Streaming error:", error);
      setChats((prevChats) => [
        ...prevChats,
        [
          {
            role: "assistant",
            content: "Sorry, something went wrong. Please try again.",
          },
        ],
      ]);
    } finally {
      setChatLoading(false);
      chatInputRef.current.focus();
    }
  };

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chats]);

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !chatLoading) {
      handleSendMessage();
    }
  };

  // useEffect(() => {
  //   chatInputRef.current.focus();
  // }, []);

  const containerClass = () => {
    if (isPopup) return 'fixed bottom-6 right-6 z-[9999] w-[440px] max-w-[92vw] h-[72vh] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col';
    if (anchored === 'inline') return `w-full bg-white border border-gray-200 rounded-2xl shadow flex flex-col ${isExpanded ? 'h-[160px] overflow-hidden' : ''}`;
    return `${anchored ? 'absolute' : 'fixed'} bottom-6 right-6 z-[9999] w-[440px] max-w-[92vw] h-[72vh] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col`;
  };

  return (
    <div>
      {isOpen && (
        <div className={containerClass()}>
          <div
            className="flex items-center justify-between p-3 border-b border-gray-100 bg-white rounded-t-2xl cursor-pointer"
            onClick={() => !isPopup && setIsExpanded((v) => !v)}
            aria-expanded={isExpanded}
          >
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <IconBrain />
              AI Smart Assistant
            </p>
            <span className="flex items-center gap-2">
              {!isPopup ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsExpanded(true); setIsPopup(true); }}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                  aria-label="Open larger popup"
                  title="Open larger popup"
                >
                  <IconOpen />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsPopup(false); }}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                  aria-label="Close popup"
                  title="Close popup"
                >
                  <IconClose />
                </button>
              )}
              {!isPopup && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsExpanded((v) => !v); }}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                  aria-label="Toggle"
                >
                  {isExpanded ? <IconChevronDown /> : <IconChevronUp />}
                </button>
              )}
            </span>
          </div>

          {(isExpanded || isPopup) && (
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50" style={{ maxHeight: isPopup ? 'calc(100% - 120px)' : '90px' }}>
            {chats.map((chat, idx) => (
              <div
                key={idx}
                className={`flex ${
                  chat[0].role === "user" ? "justify-end" : "justify-start"
                } mb-3`}
              >
                <ChatBubble>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: mdParser.render(chat[0].content),
                    }}
                  />
                </ChatBubble>
              </div>
            ))}
          </div>
          )}

          {(isExpanded || isPopup) && (
          <div className="flex flex-col gap-2 p-3 border-t border-gray-100 bg-white rounded-b-2xl">
            {usageError && (
              <div className="text-sm text-red-600">{usageError}</div>
            )}
            <div className="flex items-center gap-3">
              <input
                ref={chatInputRef}
                type="text"
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => setIsPopup(true)}
                placeholder="Ask your question..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
                disabled={chatLoading}
              />
            <div className="flex-shrink-0">
              {chatLoading ? (
                <div className="p-2 flex items-center justify-center h-[40px] w-[40px] rounded-lg">
                  <Loader size="sm" color="gray" />
                </div>
              ) : (
                <button
                  onClick={handleSendMessage}
                  disabled={!userMessage.trim()}
                  className="p-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center h-[40px] w-[40px]"
                  aria-label="Send Message"
                >
                  <IconSend />
                </button>
              )}
            </div>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ChatDocumentUpload;
